import { useEffect, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  VStack,
} from '@chakra-ui/react'

import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type CreateAssetModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: (asset: any) => void
  clientCode?: string
}

export function CreateAssetModal({ isOpen, onClose, onSuccess, clientCode }: CreateAssetModalProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [brands, setBrands] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [functions, setFunctions] = useState<any[]>([])
  const [brandModels, setBrandModels] = useState<any[]>([])
  const [specificCategories, setSpecificCategories] = useState<any[]>([])

  const [assetForm, setAssetForm] = useState({
    brand_id: '',
    category_id: '',
    tag_number: '',
    model: '',
    serial_number: '',
    function: '',
    manufacturer: '',
    sw_provider: '',
    warranty_date: '',
    order_received_date: '',
    ram: '',
    disk_space: '',
    os_version: '',
    ms_office: '',
    processor: '',
    cpu: '',
    ip_address: '',
    mac_address: '',
    active: true,
  })

  const getSpecificCategoryOptions = (categoryName: string) => {
    if (!categoryName || !specificCategories || !Array.isArray(specificCategories)) {
      return []
    }

    const categoryParentSlugMap: Record<string, string> = {
      'SW Provider': 'sw-provider',
      'Disk Space': 'disk-space',
      'MS Office': 'ms-office',
      RAM: 'ram',
    }

    const parentSlug = categoryParentSlugMap[categoryName]
    if (!parentSlug) {
      return []
    }

    return specificCategories
      .filter((cat: any) => {
        if (!cat?.name) return false
        const slug = cat.parent_slug || cat.parent?.slug
        if (slug) return String(slug) === parentSlug
        const parentName = cat.parent?.name || cat.parent_name
        return (
          parentName &&
          String(parentName).toLowerCase() === String(categoryName).toLowerCase()
        )
      })
      .sort((a: any, b: any) => {
        if (!a.name || !b.name) return 0
        return a.name.localeCompare(b.name)
      })
  }

  useEffect(() => {
    if (isOpen) {
      setAssetForm({
        brand_id: '',
        category_id: '',
        tag_number: '',
        model: '',
        serial_number: '',
        function: '',
        manufacturer: '',
        sw_provider: '',
        warranty_date: '',
        order_received_date: '',
        ram: '',
        disk_space: '',
        os_version: '',
        ms_office: '',
        processor: '',
        cpu: '',
        ip_address: '',
        mac_address: '',
        active: true,
      })
      setCategories([])
      setFunctions([])
      setBrandModels([])

      const config: any = {}
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      }

      apiClient.get('/options/brands', config)
        .then((response) => {
          const data = response.data
          const brandsArray = Array.isArray(data) ? data : (data?.data || [])
          setBrands(brandsArray)
        })
        .catch((error) => {
          console.error('Error loading brands:', error)
          showError(error?.response?.data?.message || 'Failed to load brands. Please ensure a request is selected.')
          setBrands([])
        })

      apiClient.get('/options/specific-categories', config)
        .then((response) => {
          const data = response.data

          let categoriesArray: any[] = []
          if (Array.isArray(data)) {
            categoriesArray = data
          } else if (data?.data && Array.isArray(data.data)) {
            categoriesArray = data.data
          } else if (data?.data && typeof data.data === 'object') {
            categoriesArray = Object.values(data.data)
          }

          const validCategories = categoriesArray.filter((cat: any) => {
            return cat && cat.name && cat.name.trim() !== '' && cat.name !== null && cat.name !== 'null'
          })

          setSpecificCategories(validCategories)
        })
        .catch((error) => {
          console.error('Error loading specific categories:', error)
          showError(error?.response?.data?.message || 'Failed to load categories. Please ensure a request is selected.')
          setSpecificCategories([])
        })
    }
  }, [isOpen, clientCode, showError])

  useEffect(() => {
    if (assetForm.brand_id && brands.length > 0) {
      const selectedBrand = brands.find(b => b.id.toString() === assetForm.brand_id)
      if (selectedBrand?.categories) {
        try {
          const categoriesData = JSON.parse(selectedBrand.categories)
          setCategories(categoriesData)
        } catch {
          setCategories([])
        }
      } else {
        setCategories([])
      }
      setAssetForm(prev => ({ ...prev, category_id: '', model: '' }))
    }
  }, [assetForm.brand_id, brands])

  useEffect(() => {
    if (assetForm.category_id) {
      const config: any = {}
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      }

      apiClient.get(`/options/functions/${assetForm.category_id}`, config)
        .then((response) => {
          const responseData = response.data

          let functionsArray: any[] = []
          if (Array.isArray(responseData)) {
            functionsArray = responseData
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            functionsArray = responseData.data
          } else if (responseData?.data && typeof responseData.data === 'object') {
            functionsArray = Object.values(responseData.data)
          }

          const validFunctions = functionsArray.filter((f: any) => {
            return f && f.name && f.name.trim() !== '' && f.name !== null && f.name !== 'null'
          })

          setFunctions(validFunctions)
        })
        .catch((error) => {
          console.error('Error loading functions:', error)
          showError(error?.response?.data?.message || 'Failed to load functions')
          setFunctions([])
        })
    } else {
      setFunctions([])
    }
  }, [assetForm.category_id, clientCode, showError])

  useEffect(() => {
    if (assetForm.brand_id && assetForm.category_id) {
      const config: any = {
        params: { brand_id: assetForm.brand_id, category_id: assetForm.category_id },
      }
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      }

      apiClient.get('/options/brand-models', config)
        .then(({ data }) => {
          const modelsArray = Array.isArray(data) ? data : (data?.data || [])
          setBrandModels(modelsArray)
        })
        .catch((error) => {
          console.error('Error loading brand models:', error)
          showError(error?.response?.data?.message || 'Failed to load brand models')
          setBrandModels([])
        })
    } else {
      setBrandModels([])
    }
  }, [assetForm.brand_id, assetForm.category_id, clientCode, showError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!assetForm.brand_id || !assetForm.category_id || !assetForm.tag_number || !assetForm.model || !assetForm.serial_number) {
      showError('Please fill in all required fields (Brand, Category, Tag Number, Model, Serial Number)')
      return
    }

    const selectedBrand = brands.find(b => b.id.toString() === assetForm.brand_id.toString())
    const selectedCategory = categories.find(c => c.id.toString() === assetForm.category_id.toString())

    const tempAsset = {
      id: -Date.now(),
      tag_number: assetForm.tag_number,
      model: assetForm.model,
      serial_number: assetForm.serial_number,
      brand_name: selectedBrand?.name || selectedBrand?.label || '',
      category_name: selectedCategory?.name || selectedCategory?.label || '',
      function: assetForm.function || null,
      brand_id: assetForm.brand_id,
      category_id: assetForm.category_id,
      manufacturer: assetForm.manufacturer || null,
      sw_provider: assetForm.sw_provider || null,
      warranty_date: assetForm.warranty_date || null,
      order_received_date: assetForm.order_received_date || null,
      ram: assetForm.ram || null,
      disk_space: assetForm.disk_space || null,
      os_version: assetForm.os_version || null,
      ms_office: assetForm.ms_office || null,
      processor: assetForm.processor || null,
      cpu: assetForm.cpu || null,
      ip_address: assetForm.ip_address || null,
      mac_address: assetForm.mac_address || null,
      active: assetForm.active,
      _isTemporary: true,
    }

    showSuccess('Asset added to list')
    onSuccess(tempAsset)

    setAssetForm({
      brand_id: '',
      category_id: '',
      tag_number: '',
      model: '',
      serial_number: '',
      function: '',
      manufacturer: '',
      sw_provider: '',
      warranty_date: '',
      order_received_date: '',
      ram: '',
      disk_space: '',
      os_version: '',
      ms_office: '',
      processor: '',
      cpu: '',
      ip_address: '',
      mac_address: '',
      active: true,
    })
    setCategories([])
    setFunctions([])
    setBrandModels([])
  }

  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  } as const

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Create New Asset</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody maxH="calc(90vh - 140px)" overflowY="auto">
            {!clientCode && (
              <Alert status="warning" mb={4} size="sm">
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  Please select a request first to ensure dropdowns load correctly.
                </AlertDescription>
              </Alert>
            )}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Brand</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.brand_id}
                  onChange={(e) => setAssetForm({ ...assetForm, brand_id: e.target.value, category_id: '', model: '' })}
                  {...inputBaseProps}
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Category / Machine Type</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.category_id}
                  onChange={(e) => setAssetForm({ ...assetForm, category_id: e.target.value, model: '' })}
                  isDisabled={!assetForm.brand_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id.toString()}>
                      {category?.name ?? category?.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Asset Tag / POS TID Number</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.tag_number}
                  onChange={(e) => setAssetForm({ ...assetForm, tag_number: e.target.value.toUpperCase() })}
                  placeholder="e.g. POS-2024-001 or NA"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Model</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.model}
                  onChange={(e) => setAssetForm({ ...assetForm, model: e.target.value })}
                  isDisabled={!assetForm.brand_id || !assetForm.category_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Model</option>
                  {brandModels.map(model => (
                    <option key={model.id} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Serial Number</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.serial_number}
                  onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                  placeholder="e.g. SN123456789"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Function</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.function}
                  onChange={(e) => setAssetForm({ ...assetForm, function: e.target.value })}
                  isDisabled={!assetForm.category_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Function</option>
                  {functions.filter(f => f && f.name).map(func => (
                    <option key={func.name} value={func.name.toString()}>
                      {func.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Manufacturer</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.manufacturer}
                  onChange={(e) => setAssetForm({ ...assetForm, manufacturer: e.target.value })}
                  placeholder="e.g. Ingenico, Verifone, PAX"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Warranty Expiration Date</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={assetForm.warranty_date}
                  onChange={(e) => setAssetForm({ ...assetForm, warranty_date: e.target.value })}
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Order Received Date</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={assetForm.order_received_date}
                  onChange={(e) => setAssetForm({ ...assetForm, order_received_date: e.target.value })}
                  {...inputBaseProps}
                />
              </FormControl>
            </VStack>

            <Divider my={4} />

            <Heading size="sm" mb={4} color={colors.heading}>
              Asset Specifications
            </Heading>

            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">RAM</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.ram}
                  onChange={(e) => setAssetForm({ ...assetForm, ram: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select RAM</option>
                  {getSpecificCategoryOptions('RAM')
                    .filter(ram => ram && ram.name)
                    .sort((a, b) => {
                      const getNumericValue = (name: string) => {
                        const match = name.match(/(\d+)/)
                        return match ? parseInt(match[1]) : 0
                      }
                      return getNumericValue(a.name) - getNumericValue(b.name)
                    })
                    .map(ram => (
                      <option key={ram.id} value={ram.name}>
                        {ram.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">DISK SPACE</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.disk_space}
                  onChange={(e) => setAssetForm({ ...assetForm, disk_space: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select Disk Space</option>
                  {getSpecificCategoryOptions('Disk Space')
                    .filter(disk => disk && disk.name)
                    .sort((a, b) => {
                      const getNumericValue = (name: string) => {
                        const match = name.match(/(\d+)/)
                        return match ? parseInt(match[1]) : 0
                      }
                      return getNumericValue(a.name) - getNumericValue(b.name)
                    })
                    .map(disk => (
                      <option key={disk.id} value={disk.name}>
                        {disk.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">OS VERSION</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.os_version}
                  onChange={(e) => setAssetForm({ ...assetForm, os_version: e.target.value })}
                  placeholder="e.g. Windows 10 Pro, Ubuntu 22.04"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">MS OFFICE</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.ms_office}
                  onChange={(e) => setAssetForm({ ...assetForm, ms_office: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select MS Office</option>
                  {getSpecificCategoryOptions('MS Office')
                    .filter(office => office && office.name)
                    .map(office => (
                      <option key={office.id} value={office.name}>
                        {office.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">PROCESSOR</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.processor}
                  onChange={(e) => setAssetForm({ ...assetForm, processor: e.target.value })}
                  placeholder="e.g. Intel Core i7, AMD Ryzen 5"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">CPU</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.cpu}
                  onChange={(e) => setAssetForm({ ...assetForm, cpu: e.target.value })}
                  placeholder="e.g. 2.4GHz Quad-Core"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">IP ADDRESS</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.ip_address}
                  onChange={(e) => setAssetForm({ ...assetForm, ip_address: e.target.value })}
                  placeholder="e.g. 192.168.1.100"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">MAC ADDRESS</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.mac_address}
                  onChange={(e) => setAssetForm({ ...assetForm, mac_address: e.target.value })}
                  placeholder="e.g. 00:1A:2B:3C:4D:5E"
                  {...inputBaseProps}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button type="button" onClick={onClose} variant="ghost">
                Cancel
              </Button>
              <Button type="submit" colorScheme="green">
                Add to List
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
